import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

const Overlay = styled.div`
  position: fixed; inset: 0; width: 100vw; height: 100vh; background: rgba(17, 24, 39, 0.45);
  display: grid; place-items: center; z-index: 1200;
`;
const Card = styled.div`
  background: #fff; border: 1px solid #e5e7eb; border-radius: 12px;
  padding: 16px; width: min(92vw, ${p => p.$narrow ? '560px' : '960px'}); max-width: ${p => p.$narrow ? '560px' : '960px'}; box-shadow: 0 10px 20px rgba(0,0,0,0.08);
  max-height: 92vh; overflow: auto;
`;
const Title = styled.div`font-size: 16px; font-weight: 700; color: #111827;`;
const Actions = styled.div`margin-top: 14px; display: flex; justify-content: flex-end; gap: 10px;`;
const Button = styled.button`
  height: 34px; padding: 0 12px; border-radius: 8px; border: 1px solid #2c6bed;
  background: linear-gradient(90deg, #2c6bed, #6b5cff); color: #fff; font-weight: 600; font-size: 13px; cursor: pointer;
`;
const GhostButton = styled.button`
  height: 34px; padding: 0 12px; border-radius: 8px; border: 1px solid #e5e7eb;
  background: #fff; color: #374151; font-weight: 600; font-size: 13px; cursor: pointer;
`;
const Field = styled.div`display: grid; gap: 6px; margin-top: 10px;`;
const Label = styled.label`font-size: 12px; color: #6b7280; font-weight: 600;`;
const Input = styled.input`
  height: 34px; padding: 0 10px; border: 1px solid #e5e7eb; border-radius: 8px; font-size: 13px;
`;
const Select = styled.select`
  height: 34px; padding: 0 10px; border: 1px solid #e5e7eb; border-radius: 8px; font-size: 13px;
`;
const Textarea = styled.textarea`
  min-height: 110px; padding: 8px 10px; border: 1px solid #e5e7eb; border-radius: 8px; font-size: 13px; resize: vertical;
`;
const Hint = styled.div`font-size: 12px; color: #6b7280;`;
const ErrorText = styled.div`font-size: 12px; color: #b91c1c;`;
const ErrorBanner = styled.div`
  margin-top: 10px;
  padding: 8px 10px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #7f1d1d;
  border-radius: 8px;
  font-size: 13px;
`;

const Columns = styled.div`
  display: grid; grid-template-columns: ${p => p.$single ? '1fr' : '1fr 1fr'}; gap: 16px; margin-top: 12px; align-items: start;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;
const Column = styled.div`display: grid; gap: 10px;`;

function toIsoOrNull(value) {
  if (!value) return null;
  try { return new Date(value).toISOString(); } catch { return null; }
}

export default function ScheduleEventModal({ isOpen, onClose, onSubmit, onSubmitReport, campaignId, existingEvents = [], loading, mode = 'event', initialReportDate = '', initialEmailSubject = '', initialEmailHeader = '', initialRequireAdminApproval = undefined }) {
  const [name, setName] = useState('');
  const [sequence, setSequence] = useState(1);
  const [scheduled, setScheduled] = useState('');
  const [closeDate, setCloseDate] = useState('');
  const [emailDefault, setEmailDefault] = useState('');
  const [aiAnalysis, setAiAnalysis] = useState(true);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Report scheduling state
  const [reportDate, setReportDate] = useState(initialReportDate || '');
  const [emailSubject, setEmailSubject] = useState(initialEmailSubject || '');
  const [emailHeader, setEmailHeader] = useState(initialEmailHeader || '');
  // require_admin_approval removed; default to false on submit

  const apExists = useMemo(() => (existingEvents || []).some(e => e.event_type === 'action_planning'), [existingEvents]);
  const checkinsCount = useMemo(() => (existingEvents || []).filter(e => e.event_type === 'check_in').length, [existingEvents]);
  const type = apExists ? 'check_in' : 'action_planning';
  const isValid = useMemo(() => {
    const hasName = (name || '').trim().length > 0;
    const hasScheduled = (scheduled || '').trim().length > 0;
    const hasClose = (closeDate || '').trim().length > 0;
    const hasEmail = (emailDefault || '').trim().length > 0;
    return hasName && hasScheduled && hasClose && hasEmail;
  }, [name, scheduled, closeDate, emailDefault]);

  const isReportValid = useMemo(() => {
    const hasDate = (reportDate || '').trim().length > 0;
    const hasSubject = (emailSubject || '').trim().length > 0;
    const hasHeader = (emailHeader || '').trim().length > 0;
    return hasDate && hasSubject && hasHeader;
  }, [reportDate, emailSubject, emailHeader]);

  const handleSubmit = () => {
    if (!campaignId) return;
    const missing = [];
    if (!(name || '').trim()) missing.push('Name');
    if (!(scheduled || '').trim()) missing.push('Scheduled date');
    if (!(closeDate || '').trim()) missing.push('Close date');
    if (!(emailDefault || '').trim()) missing.push('Email content');
    if (missing.length > 0) {
      setSubmitAttempted(true);
      setErrorMessage(`Please provide: ${missing.join(', ')}.`);
      return;
    }
    setErrorMessage('');
    const requiredActionsCount = type === 'action_planning' ? Math.max(1, Number(sequence) || 1) : null;
    const payload = {
      campaign: campaignId,
      event_type: type,
      name: name || (type === 'action_planning' ? 'Action Planning' : `Check-in #${checkinsCount + 1}`),
      sequence_number: type === 'check_in' ? null : requiredActionsCount,
      scheduled_date: toIsoOrNull(scheduled),
      close_date: toIsoOrNull(closeDate),
      event_config: {
        context_data: {
          include_report_data: true,
          include_action_plans: true,
          include_last_check_in: true,
        },
        response_requirements: {
          progress_update_required: true,
          satisfaction_rating_required: true,
          next_steps_required: false,
          // Enforce a minimum of 1 required action for action planning events
          minimum_required_actions: requiredActionsCount || 1,
        },
      },
      access_method: 'token',
      ai_response_analysis: !!aiAnalysis,
    };
    // Attach default custom_email_content if provided
    const hasDefault = (emailDefault || '').trim().length > 0;
    if (hasDefault) {
      payload.custom_email_content = { default: emailDefault };
    }
    onSubmit?.(payload);
  };

  const handleSubmitReport = () => {
    if (!campaignId) return;
    const missing = [];
    if (!(reportDate || '').trim()) missing.push('Report date');
    if (!(emailSubject || '').trim()) missing.push('Email subject');
    if (!(emailHeader || '').trim()) missing.push('Email header');
    if (missing.length > 0) {
      setSubmitAttempted(true);
      setErrorMessage(`Please provide: ${missing.join(', ')}.`);
      return;
    }
    setErrorMessage('');
    onSubmitReport?.({
      report_send_date: toIsoOrNull(reportDate),
      custom_subject: emailSubject,
      header_text: emailHeader,
      require_admin_approval: false,
    });
  };

  // Clear internal state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setName('');
      setSequence(1);
      setScheduled('');
      setCloseDate('');
      setEmailDefault('');
      setAiAnalysis(true);
      setSubmitAttempted(false);
      setErrorMessage('');
      setReportDate(initialReportDate || '');
      setEmailSubject(initialEmailSubject || '');
      setEmailHeader(initialEmailHeader || '');
    }
  }, [isOpen]);

  if (!isOpen) return null;
  return createPortal(
    <Overlay onClick={onClose}>
      <Card onClick={(e) => e.stopPropagation()} $narrow={mode === 'report_delivery'}>
        <Title>{mode === 'report_delivery' ? 'Schedule Report Delivery' : 'Schedule Event'}</Title>
        {!!errorMessage && (
          <ErrorBanner role="alert" aria-live="assertive">{errorMessage}</ErrorBanner>
        )}

        {mode === 'event' ? (
          <Columns>
          <Column>
            {/* Type is auto-determined based on whether an action plan exists */}
            <Field>
              <Label>Type</Label>
              <Input value={type === 'action_planning' ? 'Action planning' : 'Check in'} readOnly />
              <Hint>Auto-selected: one action planning per campaign, then check-ins.</Hint>
            </Field>

            <Field>
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={type === 'action_planning' ? 'Action Planning' : `Check-in #${checkinsCount + 1}`} />
              {submitAttempted && !(name || '').trim() && (
                <ErrorText>Name is required.</ErrorText>
              )}
            </Field>

            <Field>
              <Label>Scheduled date</Label>
              <Input type="datetime-local" value={scheduled} onChange={(e) => setScheduled(e.target.value)} />
              {submitAttempted && !(scheduled || '').trim() && (
                <ErrorText>Scheduled date is required.</ErrorText>
              )}
            </Field>

            <Field>
              <Label>Custom email content (default)</Label>
              <Textarea
                value={emailDefault}
                onChange={(e) => setEmailDefault(e.target.value)}
                placeholder="<p>Reminder: attend the workshop for extra support.</p>"
              />
              <Hint>Optional. Supports HTML.</Hint>
              {submitAttempted && !(emailDefault || '').trim() && (
                <ErrorText>Email content is required.</ErrorText>
              )}
            </Field>
          </Column>

          <Column>
            {type === 'action_planning' && (
              <Field>
                <Label># of required actions</Label>
                <Select value={String(sequence)} onChange={(e) => setSequence(Number(e.target.value))}>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <option key={i+1} value={i+1}>{i+1}</option>
                  ))}
                </Select>
                <Hint>For action planning, select 1–6.</Hint>
              </Field>
            )}

            <Field>
              <Label>Close date</Label>
              <Input type="datetime-local" value={closeDate} onChange={(e) => setCloseDate(e.target.value)} />
              {submitAttempted && !(closeDate || '').trim() && (
                <ErrorText>Close date is required.</ErrorText>
              )}
            </Field>

            <Field>
              <Label>Enable AI recommendations</Label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={!!aiAnalysis} onChange={(e) => setAiAnalysis(e.target.checked)} />
                <span style={{ fontSize: 12, color: '#6b7280' }}>{aiAnalysis ? 'Enabled' : 'Disabled'}</span>
              </div>
            </Field>
          </Column>
        </Columns>

        ) : (
          <Columns $single>
            <Column>
              <Field>
                <Label>Send date</Label>
                <Input type="datetime-local" value={reportDate} onChange={(e) => setReportDate(e.target.value)} />
                {submitAttempted && !(reportDate || '').trim() && (
                  <ErrorText>Send date is required.</ErrorText>
                )}
              </Field>
              <Field>
                <Label>Email subject</Label>
                <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
                {submitAttempted && !(emailSubject || '').trim() && (
                  <ErrorText>Subject is required.</ErrorText>
                )}
              </Field>
              <Field>
                <Label>Email body</Label>
                <Textarea value={emailHeader} onChange={(e) => setEmailHeader(e.target.value)} />
                {submitAttempted && !(emailHeader || '').trim() && (
                  <ErrorText>Body is required.</ErrorText>
                )}
              </Field>
            </Column>
          </Columns>
        )}

        <Actions>
          <GhostButton type="button" onClick={onClose} disabled={loading}>Cancel</GhostButton>
          {mode === 'report_delivery' ? (
            <Button type="button" onClick={handleSubmitReport} disabled={loading}>{loading ? 'Saving…' : 'Save'}</Button>
          ) : (
            <Button type="button" onClick={handleSubmit} disabled={loading}>{loading ? 'Saving…' : 'Schedule'}</Button>
          )}
        </Actions>
      </Card>
    </Overlay>,
    document.body
  );
}


